import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api'
import type { AssistantAgentDefinition, AssistantProviderCard, AssistantMessage } from '@/api'
import { useMissionContext } from '@/hooks/useMissionContext'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

type PendingAction = {
  actionId: string
  toolName: string
  toolArgs: Record<string, unknown>
}

function pickBestAgent(agents: AssistantAgentDefinition[], route: string): AssistantAgentDefinition | null {
  let best: AssistantAgentDefinition | null = null
  let bestLen = -1
  for (const agent of agents) {
    const prefixes = agent.route_match?.prefixes || []
    for (const prefix of prefixes) {
      if (route.startsWith(prefix) && prefix.length > bestLen) {
        best = agent
        bestLen = prefix.length
      }
    }
  }
  return best
}

function sseParseEvents(buffer: string): { events: Array<{ event: string; data: string }>; rest: string } {
  const events: Array<{ event: string; data: string }> = []
  const parts = buffer.split('\n\n')
  const rest = parts.pop() || ''
  for (const part of parts) {
    const lines = part.split('\n')
    let event = 'message'
    let data = ''
    for (const line of lines) {
      if (line.startsWith('event:')) event = line.slice('event:'.length).trim()
      if (line.startsWith('data:')) data += line.slice('data:'.length).trim()
    }
    if (data) events.push({ event, data })
  }
  return { events, rest }
}

export function AssistantDrawer({ open, onOpenChange }: Props) {
  const location = useLocation()
  const { selectedMissionId } = useMissionContext()

  const { data: agentsResponse } = useQuery({
    queryKey: ['assistant', 'agents'],
    queryFn: () => api.assistant.agents.list(),
    enabled: open,
  })

  const { data: modelsResponse } = useQuery({
    queryKey: ['assistant', 'models'],
    queryFn: () => api.assistant.models.list(),
    enabled: open,
  })

  const agents = agentsResponse?.agents ?? []
  const providers = modelsResponse?.providers ?? []

  const [agentId, setAgentId] = useState<string | null>(null)
  const [providerId, setProviderId] = useState<string | null>(null)
  const [modelId, setModelId] = useState<string | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [messages, setMessages] = useState<AssistantMessage[]>([])
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null)

  const [draft, setDraft] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [isSending, setIsSending] = useState(false)

  const bottomRef = useRef<HTMLDivElement | null>(null)

  const selectedProvider: AssistantProviderCard | null = useMemo(() => {
    if (!providerId) return providers[0] ?? null
    return providers.find(p => p.integration_id === providerId) ?? null
  }, [providerId, providers])

  useEffect(() => {
    if (!open) return
    if (!agentId && agents.length > 0) {
      const best = pickBestAgent(agents, location.pathname)
      setAgentId(best?.agent_id ?? agents[0].agent_id)
    }
  }, [open, agents, agentId, location.pathname])

  useEffect(() => {
    if (!open) return
    if (!providerId && providers.length > 0) {
      setProviderId(providers[0].integration_id)
    }
  }, [open, providers, providerId])

  useEffect(() => {
    if (!open) return
    if (!modelId && selectedProvider?.models?.length) {
      setModelId(selectedProvider.models[0].id)
    }
  }, [open, selectedProvider, modelId])

  useEffect(() => {
    if (!open) return
    if (!sessionId) {
      setMessages([])
      return
    }
    api.assistant.sessions.messages(sessionId)
      .then((r) => {
        setMessages(prev => (prev.length === 0 ? r.messages : prev))
      })
      .catch(() => {
        setMessages(prev => prev)
      })
  }, [open, sessionId])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length, open])

  const startSessionIfNeeded = async (): Promise<string> => {
    if (sessionId) return sessionId
    if (!selectedMissionId) throw new Error('Select a mission first')
    if (!providerId || !modelId) throw new Error('Select a model first')

    const created = await api.assistant.sessions.create({
      mission_id: selectedMissionId,
      agent_id: agentId,
      provider_integration_id: providerId,
      model_id: modelId,
      title: null,
    })
    setSessionId(created.session_id)
    return created.session_id
  }

  const send = async () => {
    if (!draft.trim()) return
    if (!selectedMissionId) return

    setIsSending(true)
    try {
      const sid = await startSessionIfNeeded()

      const uploadedIds: string[] = []
      for (const f of files) {
        const uploaded = await api.assistant.sessions.uploadAttachment(sid, f)
        uploadedIds.push(uploaded.attachment_id)
      }
      setFiles([])

      const userMessage: AssistantMessage = {
        message_id: crypto.randomUUID(),
        session_id: sid,
        role: 'user',
        content: draft,
        content_json: { ui_context: { route: location.pathname, entity_refs: [] }, attachment_ids: uploadedIds },
        created_at: new Date().toISOString(),
      }
      setMessages(prev => [...prev, userMessage])
      setDraft('')

      const correlationId = crypto.randomUUID()
      const response = await fetch(`/api/v1/assistant/sessions/${sid}/messages:stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Mission-Context-ID': selectedMissionId,
          'X-Correlation-ID': correlationId,
        },
        body: JSON.stringify({
          content: userMessage.content,
          ui_context: { route: location.pathname, entity_refs: [] },
          attachment_ids: uploadedIds,
          context_includes: [],
        }),
      })

      if (!response.ok || !response.body) {
        throw new Error(`Assistant stream failed: HTTP ${response.status}`)
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buf = ''

      while (true) {
        const { value, done } = await reader.read()
        if (done) break
        buf += decoder.decode(value, { stream: true })
        const parsed = sseParseEvents(buf)
        buf = parsed.rest

        for (const evt of parsed.events) {
          if (evt.event === 'message.delta') {
            const data = JSON.parse(evt.data) as { message_id: string; delta: string }
            setMessages(prev => {
              const idx = prev.findIndex(m => m.message_id === data.message_id)
              if (idx === -1) {
                return [
                  ...prev,
                  {
                    message_id: data.message_id,
                    session_id: sid,
                    role: 'assistant',
                    content: data.delta,
                    content_json: {},
                    created_at: new Date().toISOString(),
                  },
                ]
              }
              const next = [...prev]
              const existing = next[idx]
              next[idx] = {
                ...existing,
                content: `${existing.content || ''}${data.delta}`,
              }
              return next
            })
          } else if (evt.event === 'tool.action_required') {
            const data = JSON.parse(evt.data) as { action_id: string; tool_name: string; tool_args: Record<string, unknown> }
            setPendingAction({ actionId: data.action_id, toolName: data.tool_name, toolArgs: data.tool_args })
          } else if (evt.event === 'tool.result') {
            const data = JSON.parse(evt.data) as { tool_name: string; result: unknown }
            setMessages(prev => [
              ...prev,
              {
                message_id: crypto.randomUUID(),
                session_id: sid,
                role: 'tool',
                content: JSON.stringify({ tool_name: data.tool_name, result: data.result }, null, 2),
                content_json: { tool_name: data.tool_name },
                created_at: new Date().toISOString(),
              },
            ])
          } else if (evt.event === 'error') {
            const data = JSON.parse(evt.data) as { message: string }
            setMessages(prev => [
              ...prev,
              {
                message_id: crypto.randomUUID(),
                session_id: sid,
                role: 'system',
                content: `Error: ${data.message}`,
                content_json: {},
                created_at: new Date().toISOString(),
              },
            ])
          }
        }
      }
    } catch (e) {
      setMessages(prev => [
        ...prev,
        {
          message_id: crypto.randomUUID(),
          session_id: sessionId || 'unknown',
          role: 'system',
          content: e instanceof Error ? e.message : 'Failed to send message',
          content_json: {},
          created_at: new Date().toISOString(),
        },
      ])
    } finally {
      setIsSending(false)
    }
  }

  const approvePending = async () => {
    if (!pendingAction) return
    try {
      const result = await api.assistant.sessions.approve(pendingAction.actionId)
      setMessages(prev => [
        ...prev,
        {
          message_id: crypto.randomUUID(),
          session_id: result.session_id,
          role: 'tool',
          content: JSON.stringify({ action: 'approved', ...result }, null, 2),
          content_json: { tool_name: result.tool_name },
          created_at: new Date().toISOString(),
        },
      ])
    } finally {
      setPendingAction(null)
    }
  }

  const rejectPending = async () => {
    if (!pendingAction) return
    try {
      const result = await api.assistant.sessions.reject(pendingAction.actionId)
      setMessages(prev => [
        ...prev,
        {
          message_id: crypto.randomUUID(),
          session_id: result.session_id,
          role: 'system',
          content: `Rejected action: ${result.tool_name}`,
          content_json: {},
          created_at: new Date().toISOString(),
        },
      ])
    } finally {
      setPendingAction(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="left-auto top-0 right-0 h-screen max-h-screen w-full max-w-[520px] translate-x-0 translate-y-0 rounded-none sm:rounded-none p-4">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3">
            <span>Assistant</span>
            {selectedMissionId ? (
              <Badge variant="outline" className="font-mono text-[10px]">
                Mission: {selectedMissionId.slice(0, 8)}…
              </Badge>
            ) : (
              <Badge variant="secondary">No mission selected</Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-1">
              <label className="text-xs text-muted-foreground">Agent</label>
              <Select value={agentId || undefined} onValueChange={(v) => setAgentId(v)}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Select agent" />
                </SelectTrigger>
                <SelectContent>
                  {agents.map(a => (
                    <SelectItem key={a.agent_id} value={a.agent_id}>
                      {a.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-1">
              <label className="text-xs text-muted-foreground">Provider</label>
              <Select value={providerId || undefined} onValueChange={(v) => {
                setProviderId(v)
                setModelId(null)
              }}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Select provider" />
                </SelectTrigger>
                <SelectContent>
                  {providers.map(p => (
                    <SelectItem key={p.integration_id} value={p.integration_id}>
                      {p.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-1">
              <label className="text-xs text-muted-foreground">Model</label>
              <Select value={modelId || undefined} onValueChange={(v) => setModelId(v)}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Select model" />
                </SelectTrigger>
                <SelectContent>
                  {(selectedProvider?.models || []).map(m => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {pendingAction && (
            <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2">
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-medium">Action Required</div>
                <Badge variant="secondary" className="font-mono text-[10px]">
                  {pendingAction.toolName}
                </Badge>
              </div>
              <pre className="text-xs overflow-auto max-h-32 bg-background/60 border border-border rounded p-2">
                {JSON.stringify(pendingAction.toolArgs, null, 2)}
              </pre>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={rejectPending}>
                  Reject
                </Button>
                <Button variant="glow" size="sm" onClick={approvePending}>
                  Approve
                </Button>
              </div>
            </div>
          )}

          <div className="h-[55vh] overflow-auto rounded-lg border border-border bg-background p-3 space-y-3">
            {messages.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                Start a conversation. Context is minimal by default (mission + route).
              </div>
            ) : (
              messages.map(m => (
                <div key={m.message_id} className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Badge variant={m.role === 'assistant' ? 'default' : m.role === 'user' ? 'secondary' : 'outline'}>
                      {m.role}
                    </Badge>
                    <span className="text-[10px] text-muted-foreground font-mono">
                      {new Date(m.created_at).toLocaleTimeString()}
                    </span>
                  </div>
                  <pre className="whitespace-pre-wrap text-sm leading-relaxed">
                    {m.content || ''}
                  </pre>
                </div>
              ))
            )}
            <div ref={bottomRef} />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <label className="text-xs text-muted-foreground">Attachments (≤10MB each)</label>
              <Input
                type="file"
                multiple
                onChange={(e) => setFiles(Array.from(e.target.files || []))}
              />
            </div>
            {files.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {files.map((f) => (
                  <Badge key={`${f.name}-${f.size}`} variant="outline" className="font-mono text-[10px]">
                    {f.name} ({Math.round(f.size / 1024)} KB)
                  </Badge>
                ))}
              </div>
            )}
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Ask the assistant…"
              className="min-h-[96px]"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => {
                setSessionId(null)
                setMessages([])
                setPendingAction(null)
              }}>
                New Session
              </Button>
              <Button variant="glow" onClick={send} disabled={isSending || !draft.trim()}>
                {isSending ? 'Sending…' : 'Send'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
